import React, { useEffect, useRef, useState } from 'react';
import { Upload, Loader2, Sparkles, X, AlertCircle } from 'lucide-react';
import { getApiEndpoint } from '../../utils/apiUrl';

interface Props {
  mode: 'package' | 'itinerary';
  onResult: (data: Record<string, any>) => void;
  onClose: () => void;
}

export default function AiImageAnalyzer({ mode, onResult, onClose }: Props) {
  const [image, setImage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const token = localStorage.getItem('token') || '';
    fetch(getApiEndpoint('/api/ai/usage'), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((json) => {
        if (json.remaining != null) setRemaining(json.remaining);
      })
      .catch(() => {});
  }, []);

  const processFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Veuillez sélectionner une image valide.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('L\'image ne doit pas dépasser 10 Mo.');
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      setImage(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const analyze = async () => {
    if (!image) return;
    setIsProcessing(true);
    setError(null);

    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(getApiEndpoint('/api/ai/analyze-image'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ image, mode }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erreur serveur');

      if (json.remaining != null) setRemaining(json.remaining);
      onResult(json.data);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'analyse.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-600" />
            <h3 className="font-semibold">
              {mode === 'itinerary' ? 'Remplir l\'itinéraire par AI' : 'Remplir le package par AI'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-500">
            Importez une capture d'écran ou une photo d'une offre de voyage.
            L'IA remplira automatiquement les champs détectés.
          </p>
          {remaining != null && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${remaining === 0 ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-700'}`}>
              <Sparkles className="w-3.5 h-3.5" />
              {remaining === 0
                ? 'Limite atteinte (5/5 utilisées). Réessayez demain.'
                : `${remaining} analyse${remaining !== 1 ? 's' : ''} restante${remaining !== 1 ? 's' : ''} aujourd'hui (max 5/jour)`}
            </div>
          )}

          {!image ? (
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className="aspect-video rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/30 transition-all"
            >
              <Upload className="w-8 h-8 text-gray-400" />
              <div className="text-center">
                <p className="font-medium text-sm">Cliquez ou glissez une image</p>
                <p className="text-xs text-gray-400 mt-1">PNG, JPG jusqu'à 10 Mo</p>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
              />
            </div>
          ) : (
            <div className="relative rounded-xl overflow-hidden border">
              <img src={image} alt="Preview" className="w-full max-h-64 object-contain bg-gray-50" />
              <button
                onClick={() => {
                  setImage(null);
                  setError(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-black/70 text-white rounded-full"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-lg flex items-start gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <p>{error}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={analyze}
            disabled={!image || isProcessing || remaining === 0}
            className="px-4 py-2 text-sm text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Analyse en cours...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Analyser
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
